import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import { StandAdministration } from "./StandAdministration";
import "./stand-team.css";

type TeamEvent = {
  id: number;
  actor_user_id: string | null;
  event_type: string;
  message: string | null;
  created_at: string;
  actor_name?: string;
};

type TeamMember = {
  id: string;
  user_id: string;
  role_key: string;
  permissions: string[];
  status: string;
  display_name: string;
  full_name: string | null;
  personal_email: string | null;
  alias: string | null;
  email: string | null;
  phone: string | null;
};
const roles: Record<string, string> = {
  stand_master: "Master do stand",
  manager: "Gestor",
  sales: "Comercial",
  operations: "Operações",
  user: "Utilizador",
};
const permissionOptions = [
  {
    key: "users.manage",
    label: "Gerir equipa",
    detail: "Criar, editar, suspender e reativar utilizadores.",
  },
  {
    key: "stock.manage",
    label: "Gerir stock",
    detail: "Criar e alterar fichas de viaturas.",
  },
  {
    key: "stock.media",
    label: "Gerir fotografias",
    detail: "Carregar, ordenar e remover fotografias das viaturas.",
  },
  {
    key: "documents.view",
    label: "Consultar arquivo",
    detail: "Abrir documentos autorizados do stand e das viaturas.",
  },
  {
    key: "documents.manage",
    label: "Gerir arquivo",
    detail: "Carregar e organizar documentos com histórico e retenção.",
  },
  {
    key: "sales.manage",
    label: "Gerir vendas",
    detail: "Criar e acompanhar processos de venda.",
  },
  {
    key: "finance.view",
    label: "Consultar financeiro",
    detail: "Visualizar valores autorizados do stand.",
  },
  {
    key: "finance.manage",
    label: "Gerir financeiro",
    detail: "Validar movimentos e documentos financeiros.",
  },
  {
    key: "finance.costs",
    label: "Consultar custos",
    detail: "Ver os lançamentos e custos operacionais das viaturas.",
  },
  {
    key: "finance.documents",
    label: "Consultar documentos financeiros",
    detail: "Abrir comprovativos e documentos de custos autorizados.",
  },
  {
    key: "finance.margin",
    label: "Consultar margens",
    detail: "Ver margens previstas e realizadas sem expor documentos.",
  },
  {
    key: "site.manage",
    label: "Gerir site",
    detail: "Alterar conteúdos e publicação do site público.",
  },
  {
    key: "settings.manage",
    label: "Gerir configurações",
    detail: "Alterar as regras administrativas e operacionais do stand.",
  },
];

export function StandTeam({
  organizationId,
  canManage,
  userLimit,
}: {
  organizationId: string;
  canManage: boolean;
  userLimit: number | null;
}) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TeamMember | null>(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const load = async () => {
    setLoading(true);
    const result = await supabase.rpc("list_organization_team", {
      p_organization_id: organizationId,
    });
    if (result.error)
      setNotice(result.error.message ?? "Não foi possível carregar a equipa.");
    const rows = (result.data ?? []) as TeamMember[];
    setMembers(
      rows.map((row) => ({
        ...row,
        permissions: Array.isArray(row.permissions) ? row.permissions : [],
        display_name: row.display_name ?? row.full_name ?? "Utilizador",
        email: row.email ?? null,
        phone: row.phone ?? null,
      })),
    );
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 10000);
    return () => window.clearTimeout(timer);
  }, [notice]);
  const sorted = useMemo(
    () =>
      [...members].sort((a, b) =>
        a.display_name.localeCompare(b.display_name, "pt"),
      ),
    [members],
  );
  const activeUsers = members.filter(
    (member) => member.status === "active",
  ).length;
  const limitReached = userLimit !== null && activeUsers >= userLimit;
  return (
    <section className="stand-team">
      <header>
        <div>
          <p className="eyebrow">Equipa</p>
          <h2>Utilizadores e acessos</h2>
          <p>
            Defina quem entra no stand, a respetiva função e as ações
            permitidas.
          </p>
        </div>
        {canManage && (
          <button
            className="primary-button compact"
            disabled={limitReached}
            title={
              limitReached
                ? "O limite de utilizadores ativos deste plano foi atingido."
                : undefined
            }
            onClick={() => setCreating(true)}
          >
            + Criar utilizador
          </button>
        )}
      </header>
      {notice && (
        <button className="notice" role="status" aria-live="polite" onClick={() => setNotice(null)}>
          {notice} ×
        </button>
      )}
      <div className="team-guidance">
        <strong>Como funciona?</strong>
        <span>
          A função define o nível-base. As permissões acrescentam apenas
          responsabilidades específicas. Suspender preserva todo o histórico e
          impede o acesso operacional.
        </span>
      </div>
      {userLimit !== null && (
        <div className={`plan-usage ${limitReached ? "reached" : ""}`}>
          <strong>
            {activeUsers} de {userLimit} utilizadores ativos
          </strong>
          <span>
            {limitReached
              ? "Limite atingido. Suspenda um acesso ou altere o plano para ativar outro."
              : `${userLimit - activeUsers} vagas disponíveis no plano contratado.`}
          </span>
        </div>
      )}
      <StandAdministration
        organizationId={organizationId}
        canManage={canManage}
        area="team"
      />
      {loading ? (
        <div className="empty-state">A carregar equipa…</div>
      ) : sorted.length ? (
        <div className="team-list">
          {sorted.map((member) => (
            <button key={member.id} onClick={() => setSelected(member)}>
              <span className="team-avatar">
                {(member.full_name ?? member.display_name).slice(0, 1).toUpperCase()}
              </span>
              <span>
                <strong>{member.full_name ?? member.display_name}</strong>
                <small>@{member.alias ?? member.display_name} · {member.email ?? "Email profissional não indicado"}</small>
              </span>
              <em>{roles[member.role_key] ?? member.role_key}</em>
              <b className={member.status}>
                {member.status === "active"
                  ? "Ativo"
                  : member.status === "suspended"
                    ? "Suspenso"
                    : "Convidado"}
              </b>
            </button>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <strong>Este stand ainda não tem utilizadores.</strong>
          <span>
            Use “Criar utilizador” para adicionar o primeiro membro da equipa.
          </span>
        </div>
      )}
      {creating && (
        <CreateMember
          organizationId={organizationId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            setNotice(
              "Utilizador criado. A palavra-passe terá de ser alterada no primeiro acesso.",
            );
            void load();
          }}
        />
      )}
      {selected && (
        <EditMember
          organizationId={organizationId}
          member={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
          onSaved={(message) => {
            setSelected(null);
            setNotice(message);
            void load();
          }}
        />
      )}
    </section>
  );
}

function invoke(body: Record<string, unknown>) {
  return supabase.functions
    .invoke("manage-platform-user", { body })
    .then(({ data, error }) => ({
      error: error?.message ?? data?.error ?? null,
    }));
}
function PermissionFields({
  value,
  onChange,
  disabled = false,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="stand-permissions">
      {permissionOptions.map((option) => (
        <label key={option.key}>
          <input
            type="checkbox"
            checked={value.includes(option.key)}
            disabled={disabled}
            onChange={() =>
              onChange(
                value.includes(option.key)
                  ? value.filter((key) => key !== option.key)
                  : [...value, option.key],
              )
            }
          />
          <span>
            <strong>{option.label}</strong>
            <small>{option.detail}</small>
          </span>
        </label>
      ))}
    </div>
  );
}
function TeamDialog({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="modal-backdrop">
      <section className="team-dialog" role="dialog" aria-modal="true">
        <header>
          <div>
            <p className="eyebrow">Gestão da equipa</p>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          <button className="icon-button" onClick={onClose}>
            ×
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
function CreateMember({
  organizationId,
  onClose,
  onSaved,
}: {
  organizationId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [role, setRole] = useState("user"),
    [permissions, setPermissions] = useState<string[]>([]),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const form = new FormData(event.currentTarget);
    const result = await invoke({
      action: "create_stand_user",
      organization_id: organizationId,
      display_name: form.get("system_name"),
      full_name: form.get("full_name"),
      personal_email: form.get("personal_email"),
      professional_email: form.get("professional_email"),
      email: form.get("professional_email"),
      phone: form.get("phone"),
      alias: form.get("system_name"),
      system_name: form.get("system_name"),
      password: form.get("password"),
      role_key: role,
      permissions,
    });
    setSaving(false);
    if (result.error) return setError(result.error);
    onSaved();
  };
  return (
    <TeamDialog
      title="Criar utilizador"
      subtitle="O acesso é criado diretamente nesta equipa."
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <h3>Informações pessoais</h3>
        <div className="form-grid">
          <label>
            <span>Nome completo *</span>
            <input name="full_name" required autoFocus />
          </label>
          <label>
            <span>Email pessoal</span>
            <input name="personal_email" type="email" />
          </label>
          <label>
            <span>Telefone</span>
            <input name="phone" type="tel" />
          </label>
        </div>
        <h3>Informações profissionais e acesso</h3>
        <div className="form-grid">
          <label>
            <span>Nome no sistema *</span>
            <input name="system_name" placeholder="Ex.: maria.silva" required />
            <small className="form-hint">Identifica o utilizador no Stand e também pode ser usado para entrar.</small>
          </label>
          <label>
            <span>Email profissional / acesso *</span>
            <input name="professional_email" type="email" required />
          </label>
          <label>
            <span>Palavra-passe temporária *</span>
            <input name="password" type="password" minLength={10} required />
          </label>
          <label>
            <span>Função *</span>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value)}
            >
              {Object.entries(roles).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <h3>Permissões específicas</h3>
        <PermissionFields value={permissions} onChange={setPermissions} />
        {error && <p className="form-error">{error}</p>}
        <footer>
          <button className="text-button" type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary-button compact" disabled={saving}>
            {saving ? "A criar…" : "Criar utilizador"}
          </button>
        </footer>
      </form>
    </TeamDialog>
  );
}
function EditMember({
  organizationId,
  member,
  canManage,
  onClose,
  onSaved,
}: {
  organizationId: string;
  member: TeamMember;
  canManage: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [role, setRole] = useState(member.role_key),
    [status, setStatus] = useState(member.status),
    [permissions, setPermissions] = useState(member.permissions),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null),
    [resetOpen, setResetOpen] = useState(false),
    [temporaryPassword, setTemporaryPassword] = useState(""),
    [passwordConfirmation, setPasswordConfirmation] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const form = new FormData(event.currentTarget);
    const result = await invoke({
      action: "update_stand_user",
      membership_id: member.id,
      display_name: form.get("system_name"),
      full_name: form.get("full_name"),
      personal_email: form.get("personal_email"),
      professional_email: form.get("professional_email"),
      system_name: form.get("system_name"),
      alias: form.get("system_name"),
      phone: form.get("phone"),
      stand_role: role,
      status,
      permissions,
      status_reason: form.get("status_reason"),
    });
    setSaving(false);
    if (result.error) return setError(result.error);
    onSaved("Acesso do utilizador atualizado.");
  };
  const resetPassword = async () => {
    setError(null);
    if (temporaryPassword !== passwordConfirmation) {
      setError("As duas palavras-passe não coincidem.");
      return;
    }
    if (
      temporaryPassword.length < 10 ||
      !/[a-z]/.test(temporaryPassword) ||
      !/[A-Z]/.test(temporaryPassword) ||
      !/\d/.test(temporaryPassword) ||
      !/[^A-Za-z0-9\s]/.test(temporaryPassword)
    ) {
      setError("Use pelo menos 10 caracteres, incluindo maiúscula, minúscula, número e símbolo.");
      return;
    }
    setSaving(true);
    const result = await invoke({
      action: "reset_user_password",
      membership_id: member.id,
      user_id: member.user_id,
      password: temporaryPassword,
    });
    setSaving(false);
    if (result.error) return setError(result.error);
    onSaved("Palavra-passe temporária definida. Será obrigatório alterá-la no próximo acesso.");
  };
  return (
    <TeamDialog
      title={member.full_name ?? member.display_name}
      subtitle={`${roles[member.role_key] ?? member.role_key} · ${member.email ?? "Email não indicado"}`}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <h3>Informações pessoais</h3>
        <div className="form-grid">
          <label>
            <span>Nome completo</span>
            <input
              name="full_name"
              defaultValue={member.full_name ?? ""}
              disabled={!canManage}
            />
          </label>
          <label><span>Email pessoal</span><input name="personal_email" type="email" defaultValue={member.personal_email ?? ""} disabled={!canManage} /></label>
        </div>
        <h3>Informações profissionais e acesso</h3>
        <div className="form-grid">
          <label><span>Nome no sistema</span><input name="system_name" defaultValue={member.alias ?? member.display_name} disabled={!canManage} /></label>
          <label><span>Email profissional / acesso</span><input name="professional_email" type="email" defaultValue={member.email ?? ""} disabled={!canManage} /></label>
          <label>
            <span>Telefone</span>
            <input
              name="phone"
              defaultValue={member.phone ?? ""}
              disabled={!canManage}
            />
          </label>
          <label>
            <span>Função</span>
            <select
              value={role}
              disabled={!canManage}
              onChange={(event) => setRole(event.target.value)}
            >
              {Object.entries(roles).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Estado</span>
            <select
              value={status}
              disabled={!canManage}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="active">Ativo</option>
              <option value="suspended">Suspenso</option>
            </select>
          </label>
          {status !== member.status && (
            <label className="wide">
              <span>
                Motivo da {status === "suspended" ? "suspensão" : "reativação"}{" "}
                *
              </span>
              <textarea
                name="status_reason"
                minLength={5}
                required
                disabled={!canManage}
                placeholder="Explique a decisão para o histórico da equipa."
              />
            </label>
          )}
        </div>
        <h3>Permissões específicas</h3>
        <PermissionFields
          value={permissions}
          onChange={setPermissions}
          disabled={!canManage}
        />
        {canManage && (
          <section className="team-password-reset">
            <div>
              <strong>Palavra-passe</strong>
              <span>A palavra-passe atual não pode ser consultada. Pode substituí-la por uma temporária.</span>
            </div>
            {!resetOpen ? (
              <button type="button" className="secondary-button" onClick={() => setResetOpen(true)}>
                Alterar palavra-passe
              </button>
            ) : (
              <div className="team-password-reset-form">
                <label>
                  <span>Nova palavra-passe temporária *</span>
                  <input type="password" autoComplete="new-password" value={temporaryPassword} onChange={(event) => setTemporaryPassword(event.target.value)} />
                </label>
                <label>
                  <span>Confirmar palavra-passe *</span>
                  <input type="password" autoComplete="new-password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} />
                </label>
                <small>Mínimo de 10 caracteres, com maiúscula, minúscula, número e símbolo.</small>
                <div>
                  <button type="button" className="text-button" onClick={() => { setResetOpen(false); setError(null); }}>Cancelar</button>
                  <button type="button" className="primary-button compact" disabled={saving} onClick={() => void resetPassword()}>{saving ? "A guardar…" : "Guardar"}</button>
                </div>
              </div>
            )}
          </section>
        )}
        {error && <p className="form-error">{error}</p>}
        <footer>
          <button className="text-button" type="button" onClick={onClose}>
            Fechar
          </button>
          {canManage && (
            <button className="primary-button compact" disabled={saving}>
              {saving ? "A guardar…" : "Guardar"}
            </button>
          )}
        </footer>
      </form>
    </TeamDialog>
  );
}

const eventLabels: Record<string, string> = {
  created: "Utilizador criado",
  updated: "Dados atualizados",
  role_changed: "Função alterada",
  permissions_changed: "Permissões alteradas",
  suspended: "Acesso suspenso",
  reactivated: "Acesso reativado",
  access_removed: "Acesso removido",
  password_reset_required: "Palavra-passe temporária definida",
};
function AuditTrail({
  organizationId,
  userId,
}: {
  organizationId: string;
  userId: string;
}) {
  const [events, setEvents] = useState<TeamEvent[]>([]),
    [loading, setLoading] = useState(true);
  useEffect(() => {
    void (async () => {
      const result = await supabase
        .from("organization_user_events")
        .select("id,actor_user_id,event_type,message,created_at")
        .eq("organization_id", organizationId)
        .eq("target_user_id", userId)
        .order("created_at", { ascending: false })
        .limit(20);
      const rows = (result.data ?? []) as TeamEvent[];
      const actorIds = [
        ...new Set(
          rows
            .map((row) => row.actor_user_id)
            .filter((id): id is string => Boolean(id)),
        ),
      ];
      const profiles = actorIds.length
        ? await supabase
            .from("profiles")
            .select("user_id,display_name")
            .in("user_id", actorIds)
        : { data: [] };
      const names = new Map(
        (profiles.data ?? []).map((profile) => [
          profile.user_id,
          profile.display_name,
        ]),
      );
      setEvents(
        rows.map((row) => ({
          ...row,
          actor_name: row.actor_user_id
            ? (names.get(row.actor_user_id) ?? "Utilizador autorizado")
            : "Sistema",
        })),
      );
      setLoading(false);
    })();
  }, [organizationId, userId]);
  return (
    <section className="team-audit">
      <h3>Histórico de acesso</h3>
      {loading ? (
        <p>A carregar histórico…</p>
      ) : events.length ? (
        <ol>
          {events.map((event) => (
            <li key={event.id}>
              <span className={`audit-mark ${event.event_type}`} />
              <div>
                <strong>
                  {eventLabels[event.event_type] ?? event.event_type}
                </strong>
                <small>
                  {event.actor_name} ·{" "}
                  {new Intl.DateTimeFormat("pt-PT", {
                    dateStyle: "short",
                    timeStyle: "short",
                  }).format(new Date(event.created_at))}
                </small>
                {event.message && <p>{event.message}</p>}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p>Ainda não existem alterações registadas para este utilizador.</p>
      )}
    </section>
  );
}
